import { produce } from 'immer';
import { useCallback, useMemo } from 'react';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import {
  ColorStyleInput,
  LinkedStyleInputs,
  StyleInput,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import {
  StyleValueBoxDimensions,
  StyleValueColorConfig,
  StyleValueSizeConfig,
} from 'lib/inquiryStyles';
import {
  BorderStyleKeys,
  BorderStylesComponent,
  BorderStylesComponentConfig,
} from 'lib/interfaces/ui-components';

type BorderStylesAttributes = {
  'border-width': {
    label: string;
    default: StyleValueBoxDimensions;
  };
  'border-radius': {
    label: string;
    default: StyleValueSizeConfig;
  };
  'border-color': {
    label: string;
    default: StyleValueColorConfig;
  };
};

const DEFAULT_BORDER_ATTRIBUTES: BorderStylesAttributes = {
  'border-width': {
    label: 'Border width',
    default: {
      top: { value: 0, unit: 'px' },
      bottom: {
        value: 0,
        unit: 'px',
      },
      left: { value: 0, unit: 'px' },
      right: { value: 0, unit: 'px' },
    },
  },
  'border-radius': {
    label: 'Border radius',
    default: { value: 0, unit: 'px' },
  },
  'border-color': {
    label: 'Border color',
    default: {
      value: '',
      unit: 'hex',
    },
  },
};

type BorderStylesProps = {
  componentPreview: BorderStylesComponent;
  component: BorderStylesComponentConfig;
  styleVariantIndex: number;
  editable: boolean;
  submitStyle: (field: string, values: any) => void;
};

const BorderStyles = ({
  componentPreview,
  component,
  styleVariantIndex,
  editable,
  submitStyle,
}: BorderStylesProps) => {
  const borderDefaults: BorderStylesAttributes = useMemo(() => {
    const borderStyleTuples = Object.entries(DEFAULT_BORDER_ATTRIBUTES).map((styleAssignment) => {
      const [style, props] = styleAssignment;
      const defaultValue =
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        componentPreview.styles?.[style as BorderStyleKeys]?.base?.base || props.default;
      return [
        style,
        {
          ...props,
          default: defaultValue,
        },
      ];
    });

    return Object.fromEntries(borderStyleTuples);
  }, [componentPreview.styles]);

  const submitField = useCallback(
    (value: string, field: string) => {
      const savedValue: string | number = parseInt(value) >= 0 ? parseInt(value) : 0;
      submitStyle(field, {
        value: savedValue,
        unit: 'px',
      });
    },
    [submitStyle],
  );

  const submitSingleBorderWidth = useCallback(
    (value: string, field: string, root: BorderStyleKeys) => {
      const savedValue = parseInt(value);
      const newValues = produce(borderDefaults['border-width'], (newBorderWidthValues) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        newBorderWidthValues.default[field as keyof StyleValueBoxDimensions].value = savedValue;
        return newBorderWidthValues;
      });

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      submitStyle(root, newValues.default);
    },
    [submitStyle, borderDefaults],
  );

  const submitLinkedBorderWidth = useCallback(
    (value: string, root: string) => {
      const savedValue = parseInt(value);
      submitStyle(root, {
        top: { value: savedValue, unit: 'px' },
        bottom: { value: savedValue, unit: 'px' },
        left: { value: savedValue, unit: 'px' },
        right: { value: savedValue, unit: 'px' },
      });
    },
    [submitStyle],
  );

  const onChange = useCallback(
    (hex: string, field: string) => {
      submitStyle(field, { value: hex, unit: 'hex' });
    },
    [submitStyle],
  );

  return (
    <SectionAccordion title='Border'>
      <SectionRow>
        <SectionColumn>
          <LinkedStyleInputs
            label={borderDefaults['border-width'].label}
            defaultValues={borderDefaults['border-width'].default as StyleValueBoxDimensions}
            rootStyle='border-width'
            submitSingle={submitSingleBorderWidth}
            submitLinked={submitLinkedBorderWidth}
            id={`${component.name}-${styleVariantIndex}-border-width`}
            editable={editable}
          />
        </SectionColumn>
      </SectionRow>
      <SectionRow>
        <SectionColumn>
          <ColorStyleInput
            label={borderDefaults['border-color'].label}
            field='border-color'
            name={component.name}
            onChange={onChange}
            value={borderDefaults['border-color'].default.value}
            editable={editable}
          />
        </SectionColumn>
        <SectionColumn>
          <StyleInput
            field='border-radius'
            label={borderDefaults['border-radius'].label}
            defaultValue={(
              borderDefaults['border-radius'].default as StyleValueSizeConfig
            ).value.toString()}
            submit={submitField}
            name={`${component.name}-${styleVariantIndex}-border-radius`}
            editable={editable}
          />
        </SectionColumn>
      </SectionRow>
    </SectionAccordion>
  );
};

export default BorderStyles;
